<script setup lang="ts">
import {
  TranslationMode,
  type TranslatableEntityType,
  type TranslatedField,
  type TranslationResult,
} from '~/graphql/operations'
import { LanguageSelect } from '~~/shared/inputs'

const props = withDefaults(
  defineProps<{
    entityType: TranslatableEntityType
    entityId: number | null | undefined
    targetLanguageId: number | null | undefined
    fields: string[]
    sourceLanguageId?: number | null
    sourceChannelId?: number | null
    targetChannelId?: number | null
    label?: string
    size?: 'sm' | 'md'
    compact?: boolean
    persistKey?: string
  }>(),
  {
    label: 'AI fill from source',
    size: 'md',
    compact: false,
    sourceLanguageId: null,
    sourceChannelId: null,
    targetChannelId: null,
    persistKey: undefined,
  },
)

const emit = defineEmits<{
  filled: [
    payload: {
      result: TranslationResult
      translatedFields: TranslatedField[]
      sourceLanguageId: number
    },
  ]
  warning: [warnings: string[]]
  error: [error: Error]
}>()

const store = useStore()

const canTranslate = computed(() =>
  (store.auth?.user?.permissions || []).some(
    (p) =>
      p === 'translationEngine:w' ||
      p === 'translationEngine' ||
      p === '**:w' ||
      p === '**',
  ),
)

const { translateFields } = useTranslateEntityFields()
const { fetchValues } = useEntityTextFieldValues()

type ButtonState = 'idle' | 'generating' | 'generated' | 'failed'
const state = ref<ButtonState>('idle')
const errorMessage = ref<string | null>(null)
const lastWarnings = ref<string[]>([])

const sessionKey = computed(
  () =>
    props.persistKey ||
    `translation:sourceLang:${props.entityType}:${props.entityId ?? 'new'}:${props.sourceChannelId ?? 'none'}`,
)

const pickedSourceLanguageId = ref<number | null>(null)

const channelDefaultLanguageId = computed<number | null>(() => {
  if (!props.sourceChannelId) return null
  const ch = (store.channels || []).find(
    (c: any) => +c.id === +props.sourceChannelId!,
  )
  const id = ch?.defaultLanguageId
  return id ? +id : null
})

const readPersistedSource = (): number | null => {
  if (typeof window === 'undefined') return null
  const raw = sessionStorage.getItem(sessionKey.value)
  if (!raw) return null
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : null
}

const writePersistedSource = (id: number | null) => {
  if (typeof window === 'undefined') return
  if (id == null) sessionStorage.removeItem(sessionKey.value)
  else sessionStorage.setItem(sessionKey.value, String(id))
}

watch(
  () => props.sourceLanguageId,
  (v) => {
    if (v != null) pickedSourceLanguageId.value = v
  },
)

const effectiveSourceLanguageId = computed(
  () =>
    props.sourceLanguageId ??
    pickedSourceLanguageId.value ??
    channelDefaultLanguageId.value ??
    readPersistedSource() ??
    null,
)

const disabledReason = computed(() => {
  if (!canTranslate.value) return 'Missing translationEngine:w permission'
  if (!props.entityId) return 'Save the entity first to enable AI fill'
  if (!props.targetLanguageId) return 'Target language not set'
  if (!effectiveSourceLanguageId.value) return 'Pick a source language'
  if (!props.fields.length) return 'No translatable fields in scope'
  return null
})

const onClick = async () => {
  if (disabledReason.value) return
  state.value = 'generating'
  errorMessage.value = null
  lastWarnings.value = []

  try {
    const sourceLanguageId = effectiveSourceLanguageId.value!

    const sourceValues = await fetchValues({
      entityType: props.entityType,
      entityId: props.entityId!,
      languageId: sourceLanguageId,
      channelId: props.sourceChannelId ?? null,
    })
    const hasContent = sourceValues.some(
      (v) => v.value && v.value.trim().length > 0,
    )
    if (!hasContent) {
      throw new Error(
        'The selected source language has no content for this entity.',
      )
    }

    const result = await translateFields({
      entityType: props.entityType,
      entityId: props.entityId!,
      sourceLanguageId,
      targetLanguageId: props.targetLanguageId!,
      sourceChannelId: props.sourceChannelId ?? null,
      targetChannelId: props.targetChannelId ?? null,
      fields: props.fields,
      mode: TranslationMode.Inline,
    })

    lastWarnings.value = result.warnings || []
    writePersistedSource(sourceLanguageId)

    emit('filled', {
      result,
      translatedFields: result.translatedFields,
      sourceLanguageId,
    })
    if (lastWarnings.value.length) emit('warning', lastWarnings.value)
    state.value = 'generated'
  } catch (e: any) {
    state.value = 'failed'
    errorMessage.value = e?.message || 'AI fill failed'
    emit('error', e instanceof Error ? e : new Error(errorMessage.value!))
  }
}

const sourceLanguageModel = computed({
  get: () => effectiveSourceLanguageId.value ?? null,
  set: (v: number | null) => {
    pickedSourceLanguageId.value = v && v > 0 ? v : null
    state.value = 'idle'
    errorMessage.value = null
  },
})

// Editor edits the field after generation → drop the "generated" highlight.
const resetGeneratedState = () => {
  if (state.value === 'generated') state.value = 'idle'
}
defineExpose({ resetGeneratedState })
</script>

<template>
  <div v-if="canTranslate" class="flex items-center gap-2">
    <FormKit
      v-if="!compact && !sourceLanguageId"
      v-bind="new LanguageSelect({}).input()"
      v-model="sourceLanguageModel"
      type="multiselect"
      label=""
      placeholder="Translate from…"
      outer-class="!mb-0"
    />

    <button
      type="button"
      :disabled="!!disabledReason || state === 'generating'"
      :title="disabledReason || (compact ? 'AI fill this field' : label)"
      class="inline-flex items-center gap-1.5 rounded border transition focus:outline-none focus:ring-2 focus:ring-primary-500/40"
      :class="[
        compact
          ? 'p-1 text-base'
          : size === 'sm'
            ? 'px-2 py-1 text-xs'
            : 'px-3 py-1.5 text-sm',
        state === 'failed'
          ? 'border-rose-300 text-rose-700 bg-rose-50 hover:bg-rose-100'
          : state === 'generated'
            ? 'border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100'
            : 'border-primary-300 text-primary-700 bg-white hover:bg-primary-50',
        disabledReason ? 'opacity-50 cursor-not-allowed' : '',
      ]"
      @click="onClick"
    >
      <span v-if="state === 'generating'" class="inline-flex items-center">
        <i class="i-icon-park-outline:loading-one animate-spin text-base" />
        <span v-if="!compact" class="ml-1">Generating…</span>
      </span>
      <span v-else-if="state === 'generated'" class="inline-flex items-center">
        <i class="i-icon-park-outline:check-one text-base" />
        <span v-if="!compact" class="ml-1">Filled</span>
      </span>
      <span v-else class="inline-flex items-center">
        <i class="i-icon-park-outline:magic-wand text-base" />
        <span v-if="!compact" class="ml-1">{{ label }}</span>
      </span>
    </button>

    <span
      v-if="state === 'failed' && errorMessage && !compact"
      class="text-xs text-rose-700"
      :title="errorMessage"
    >
      {{ errorMessage }}
    </span>
  </div>
</template>
